import React, { useState, useEffect } from 'react';
import { configService, UserConfiguration, LearningMethod, OutputMode, LanguageMode } from '../services/configService';
import { LEARNING_METHODS, OUTPUT_MODES, LANGUAGE_MODES } from '../utils/configLabels';
import { VERB_FORMS_BY_CATEGORY, VerbFormLabel } from '../utils/verbFormLabels';
import { authenticatedFetch } from '../utils/apiUtils';
import '../styles/FormControls.css';
import './ConfigurationScreen.css';

interface ConfigurationScreenProps {
  onBack: () => void;
  onLogout: () => void;
}

function ConfigurationScreen({ onBack, onLogout }: ConfigurationScreenProps) {
  const [config, setConfig] = useState<UserConfiguration>({
    vocableNumber: 10,
    learningMethod: 'SPACED_REPETITION',
    outputMode: 'DEFAULT',
    languageMode: 'MIXED',
    includedConjugations: ['PRESENT_DICTIONARY', 'PRESENT_POLITE'],
    reduceScriptSwitching: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'general' | 'verbSettings'>('general');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setIsLoading(true);
    setError('');
    try {
      const loadedConfig = await configService.getConfiguration();
      setConfig({
        ...loadedConfig,
        languageMode: loadedConfig.languageMode ?? 'MIXED',
        includedConjugations: loadedConfig.includedConjugations ?? [],
        reduceScriptSwitching: loadedConfig.reduceScriptSwitching ?? false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate vocableNumber
    if (config.vocableNumber <= 0) {
      setError('Words per Session must be greater than 0');
      return;
    }
    
    // Validate that at least one conjugation is selected
    if (!config.includedConjugations || config.includedConjugations.length === 0) {
      setError('At least one verb conjugation must be selected');
      return;
    }
    
    setIsSaving(true);
    setError('');
    try {
      await configService.saveConfiguration(config);
      // Navigate back to main screen after successful save
      setTimeout(() => {
        onBack();
      }, 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
      setIsSaving(false);
    }
  };

  const handleVocableNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Allow empty input
    if (inputValue === '') {
      setConfig({ ...config, vocableNumber: 0 });
      return;
    }
    const value = parseInt(inputValue, 10);
    if (!isNaN(value) && value > 0) {
      setConfig({ ...config, vocableNumber: value });
    }
  };

  const handleConjugationToggle = (formValue: string) => {
    const currentConjugations = config.includedConjugations || [];
    const isIncluded = currentConjugations.includes(formValue);
    
    setConfig({
      ...config,
      includedConjugations: isIncluded
        ? currentConjugations.filter(v => v !== formValue)
        : [...currentConjugations, formValue],
    });
  };

  if (isLoading) {
    return (
      <div className="configuration-screen">
        <div className="configuration-container">
          <div className="loading-state">Loading configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration-screen">
      <div className="configuration-container">
        <div className="configuration-header">
          <h1 className="configuration-title">
            <span className="configuration-title-full">Configuration</span>
            <span className="configuration-title-short">Config</span>
          </h1>
          <div className="header-buttons">
            <button className="back-button" onClick={() => setShowLogoutDialog(true)}>
              Account
            </button>
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
          </div>
        </div>

        <div className="configuration-tabs">
          <button
            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
            disabled={isSaving}
          >
            General
          </button>
          <button
            className={`tab-button ${activeTab === 'verbSettings' ? 'active' : ''}`}
            onClick={() => setActiveTab('verbSettings')}
            disabled={isSaving}
          >
            Verb Conjugations
          </button>
        </div>

        <div className="configuration-form">
          {activeTab === 'general' && (
            <>
              <div className="form-group">
                <label htmlFor="vocableNumber" className="form-label">
                  Words per Session
                </label>
                <input
                  type="number"
                  id="vocableNumber"
                  className="form-input"
                  value={config.vocableNumber === 0 ? '' : config.vocableNumber}
                  onChange={handleVocableNumberChange}
                  disabled={isSaving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="learningMethod" className="form-label">
                  Learning Method
                </label>
                <select
                  id="learningMethod"
                  className="form-select"
                  value={config.learningMethod}
                  onChange={(e) => setConfig({ ...config, learningMethod: e.target.value as LearningMethod })}
                  disabled={isSaving}
                >
                  {LEARNING_METHODS.map(method => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="outputMode" className="form-label">
                  Display Mode
                </label>
                <select
                  id="outputMode"
                  className="form-select"
                  value={config.outputMode}
                  onChange={(e) => setConfig({ ...config, outputMode: e.target.value as OutputMode })}
                  disabled={isSaving}
                >
                  {OUTPUT_MODES.map(mode => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="languageMode" className="form-label">
                  Language Focus
                </label>
                <select
                  id="languageMode"
                  className="form-select"
                  value={config.languageMode}
                  onChange={(e) => setConfig({ ...config, languageMode: e.target.value as LanguageMode })}
                  disabled={isSaving}
                >
                  {LANGUAGE_MODES.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={config.reduceScriptSwitching ?? false}
                    onChange={(e) => setConfig({ ...config, reduceScriptSwitching: e.target.checked })}
                    disabled={isSaving}
                    style={{ width: '18px', height: '18px', cursor: isSaving ? 'not-allowed' : 'pointer' }}
                  />
                  <span>Reduce Script Switching</span>
                </label>
                <div className="form-hint" style={{ marginTop: '4px', marginLeft: '30px' }}>
                  When enabled with Mixed mode, the first half of vocables will use one language, the second half the other.
                </div>
              </div>
            </>
          )}

          {activeTab === 'verbSettings' && (
            <div className="verb-settings-content">
              <p className="verb-settings-description">
                Select which verb conjugations are allowed as answers during training sessions.
              </p>
              <div className="conjugation-groups">
                {Object.entries(VERB_FORMS_BY_CATEGORY).map(([category, forms]) => (
                  <div key={category} className="conjugation-group">
                    <h3 className="conjugation-group-title">{category}</h3>
                    <div className="conjugation-checkboxes">
                      {forms.map((form: VerbFormLabel) => {
                        const isChecked = (config.includedConjugations || []).includes(form.value);
                        return (
                          <label key={form.value} className="conjugation-checkbox-label">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleConjugationToggle(form.value)}
                              disabled={isSaving}
                              className="conjugation-checkbox"
                            />
                            <span className="conjugation-checkbox-content">
                              <span className="conjugation-japanese">{form.japaneseName}</span>
                              <span className="conjugation-english">{form.englishName}</span>
                              {form.description && (
                                <span className="conjugation-description">({form.description})</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <div className="button-row">
            <button 
              className="btn btn-secondary" 
              onClick={onBack}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Logout/Change Password Dialog */}
      {showLogoutDialog && !showChangePassword && !showDeleteAccountDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowLogoutDialog(false);
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Account Options</h3>
            <div className="confirm-actions" style={{ flexDirection: 'column', gap: '12px', alignItems: 'stretch' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowLogoutDialog(false);
                  onLogout();
                }}
                style={{ maxWidth: 'unset' }}
              >
                Log Out
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowChangePassword(true)}
                style={{ maxWidth: 'unset' }}
              >
                Change Password
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteAccountDialog(true)}
                style={{ maxWidth: 'unset' }}
              >
                Delete Account
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowLogoutDialog(false)}
                style={{ maxWidth: 'unset'}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      {showLogoutDialog && showChangePassword && !showDeleteAccountDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowChangePassword(false);
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Change Password</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="newPassword" className="form-label">
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordError('');
                  }}
                  disabled={isChangingPassword}
                  placeholder="Enter new password (12-80 characters)"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="confirmPassword" className="form-label">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordError('');
                  }}
                  disabled={isChangingPassword}
                  placeholder="Confirm new password"
                />
              </div>
              {passwordError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {passwordError}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowChangePassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  disabled={isChangingPassword}
                  style={{ maxWidth: 'unset' }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  style={{ maxWidth: 'unset' }}
                  onClick={async () => {
                    if (!newPassword || !confirmPassword) {
                      setPasswordError('Please enter both password fields');
                      return;
                    }
                    if (newPassword.length < 12 || newPassword.length > 80) {
                      setPasswordError('Password must be between 12 and 80 characters');
                      return;
                    }
                    if (newPassword !== confirmPassword) {
                      setPasswordError('Passwords do not match');
                      return;
                    }
                    setIsChangingPassword(true);
                    setPasswordError('');
                    try {
                      const response = await authenticatedFetch('/api/auth/change-password', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ password: newPassword }),
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setPasswordError(errorText || 'Failed to change password');
                        return;
                      }
                      // Success - close dialogs and show success message
                      setShowLogoutDialog(false);
                      setShowChangePassword(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                      setSuccess('Password changed successfully');
                    } catch (err) {
                      setPasswordError(err instanceof Error ? err.message : 'Failed to change password');
                    } finally {
                      setIsChangingPassword(false);
                    }
                  }}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Dialog */}
      {showLogoutDialog && showDeleteAccountDialog && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowDeleteAccountDialog(false);
            setDeleteConfirmationText('');
            setDeleteAccountError('');
          }
        }}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Delete Account</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ marginBottom: '8px' }}>
                <p className="confirm-message" style={{ marginBottom: '12px', color: 'var(--error-color)', fontWeight: '600' }}>
                  Warning: This action cannot be undone!
                </p>
                <p className="confirm-message" style={{ marginBottom: '8px' }}>
                  Deleting your account will permanently remove:
                </p>
                <ul style={{ marginLeft: '20px', marginBottom: '8px', color: 'var(--text-secondary)' }}>
                  <li>All your vocabulary entries</li>
                  <li>Your training progress and configuration</li>
                  <li>All other account data</li>
                </ul>
                <p className="confirm-message" style={{ marginTop: '12px' }}>
                  To confirm, please type <strong>"I understand"</strong> below:
                </p>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <input
                  type="text"
                  className="form-input"
                  value={deleteConfirmationText}
                  onChange={(e) => {
                    setDeleteConfirmationText(e.target.value);
                    setDeleteAccountError('');
                  }}
                  disabled={isDeletingAccount}
                  placeholder="Type 'I understand' to confirm"
                  autoFocus
                />
              </div>
              {deleteAccountError && (
                <div className="error-message" style={{ marginTop: 0 }}>
                  {deleteAccountError}
                </div>
              )}
              <div className="confirm-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteAccountDialog(false);
                    setDeleteConfirmationText('');
                    setDeleteAccountError('');
                  }}
                  disabled={isDeletingAccount}
                  style={{ maxWidth: 'unset' }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  style={{ maxWidth: 'unset' }}
                  onClick={async () => {
                    if (deleteConfirmationText.trim() !== 'I understand') {
                      setDeleteAccountError('Please type "I understand" to confirm');
                      return;
                    }
                    setIsDeletingAccount(true);
                    setDeleteAccountError('');
                    try {
                      const response = await authenticatedFetch('/api/auth/delete-user', {
                        method: 'DELETE',
                      });
                      if (!response.ok) {
                        const errorText = await response.text();
                        setDeleteAccountError(errorText || 'Failed to delete account');
                        return;
                      }
                      // Success - logout and redirect to login
                      onLogout();
                    } catch (err) {
                      setDeleteAccountError(err instanceof Error ? err.message : 'Failed to delete account');
                    } finally {
                      setIsDeletingAccount(false);
                    }
                  }}
                  disabled={isDeletingAccount || deleteConfirmationText.trim() !== 'I understand'}
                >
                  {isDeletingAccount ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConfigurationScreen;

